import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query'
import type { ProductFilters, ProductRowView } from '@today/ui-domain'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { productCapabilities } from '@/auth/product-capabilities'
import { useViewerPermissions } from '@/auth/useViewerPermissions'
import { fileUploadService } from '@/services/file-upload.service'
import { type CategoryDTO, type ProductDTO, productService } from '@/services/product.service'
import { previewRetry, previewRetryDelay } from './preview-retry'

const FILTER_KEYS: Array<keyof ProductFilters> = ['status', 'categoryId', 'code']

/** Presigned URLs live 15 min; re-fetch before they expire. */
export const IMAGE_URL_STALE_MS = 10 * 60 * 1000

/**
 * Download URLs for the given image ids (only with `file.read`); a failed id
 * maps to null. An image still being malware-scanned is retried (TO-458).
 */
export function useImageUrls(fileIds: string[], enabled: boolean): Map<string, string | null> {
	const unique = [...new Set(fileIds)]
	const results = useQueries({
		queries: unique.map((id) => ({
			queryKey: ['file-url', id],
			queryFn: () => fileUploadService.downloadUrl(id),
			enabled,
			staleTime: IMAGE_URL_STALE_MS,
			retry: previewRetry,
			retryDelay: previewRetryDelay,
		})),
	})
	return new Map(unique.map((id, i) => [id, results[i]?.data ?? null]))
}

const toRow = (p: ProductDTO, categories: CategoryDTO[], urls: Map<string, string | null>): ProductRowView => ({
	id: p.productId,
	code: p.code,
	name: p.name,
	categoryName: categories.find((c) => c.categoryId === p.categoryId)?.name ?? null,
	status: p.status,
	featured: p.featured,
	imageUrl: p.imageFileId ? (urls.get(p.imageFileId) ?? null) : null,
})

/**
 * List page state (TO-416 product-admin-ui REQ-002): filters and page live in
 * the URL; the code finder is an exact `filters[code]` lookup.
 */
export function useProductList() {
	const { t } = useTranslation('products')
	const navigate = useNavigate()
	const [params, setParams] = useSearchParams()
	const caps = productCapabilities(useViewerPermissions().permissions)
	const filters: ProductFilters = {
		status: params.get('status') ?? '',
		categoryId: params.get('categoryId') ?? '',
		code: params.get('code') ?? '',
	}
	const page = Number(params.get('page') ?? '1')
	const request: Record<string, string> = {}
	for (const key of FILTER_KEYS) if (filters[key]) request[`filters[${key}]`] = filters[key]
	Object.assign(request, { page: String(page), limit: '20' })

	const list = useQuery({
		queryKey: ['products', request],
		queryFn: () => productService.list(request),
		placeholderData: keepPreviousData,
	})
	const categories = useQuery({ queryKey: ['product-categories'], queryFn: productService.allCategories })
	const products = list.data?.data ?? []
	const urls = useImageUrls(
		products.flatMap((p) => (p.imageFileId ? [p.imageFileId] : [])),
		caps.viewImages,
	)

	const update = (next: Record<string, string>) => {
		const merged = new URLSearchParams(params)
		for (const [key, value] of Object.entries(next)) {
			if (value) merged.set(key, value)
			else merged.delete(key)
		}
		setParams(merged)
	}

	return {
		t,
		rows: products.map((p) => toRow(p, categories.data ?? [], urls)),
		filters,
		categories: (categories.data ?? []).map((c) => ({ id: c.categoryId, label: c.name })),
		page,
		pageCount: list.data?.totalPages ?? 0,
		empty: list.isSuccess && products.length === 0,
		error: list.isError,
		onFilterChange: (key: keyof ProductFilters, value: string) => update({ [key]: value, page: '' }),
		onPageChange: (next: number) => update({ page: String(next) }),
		onOpen: (id: string) => void navigate(`/products/${id}`),
		onRetry: () => void list.refetch(),
		onCreate: caps.write ? () => void navigate('/products/new') : undefined,
	}
}
